/**
 * Context Capture Utility
 * Auto-captures device, platform, and app information for feedback
 */

import { Platform } from 'react-native';
import * as Device from 'expo-device';
import Constants from 'expo-constants';
import { FeedbackContext } from '@/types/feedback.types';

/**
 * Captures all context information for feedback submission
 *
 * @param screenName - Optional screen name where feedback was triggered
 * @returns Complete context object with device, app, and platform info
 */
export const captureContext = async (screenName?: string): Promise<FeedbackContext> => {
  const appVersion = getAppVersion();
  const deviceInfo = await getDeviceInfo();
  const platform = getPlatform();

  return {
    screenName,
    appVersion,
    deviceInfo,
    platform,
  };
};

/**
 * Gets the app version from Expo config
 */
const getAppVersion = (): string => {
  try {
    return Constants.expoConfig?.version || '1.0.0';
  } catch (error) {
    console.error('[ContextCapture] Error getting app version:', error);
    return 'Unknown';
  }
};

/**
 * Gets detailed device information
 * Format: "iPhone 14 Pro, iOS 17.2" or "Pixel 7, Android 14"
 */
const getDeviceInfo = async (): Promise<string> => {
  try {
    const deviceName = Device.modelName || Device.deviceName || 'Unknown Device';
    const osVersion = Device.osVersion || 'Unknown';
    const platform = Platform.OS;

    if (platform === 'ios') {
      return `${deviceName}, iOS ${osVersion}`;
    } else if (platform === 'android') {
      return `${deviceName}, Android ${osVersion}`;
    } else {
      return `Web Browser`;
    }
  } catch (error) {
    console.error('[ContextCapture] Error getting device info:', error);
    return 'Unknown Device';
  }
};

/**
 * Gets the platform (iOS, Android, or Web)
 */
const getPlatform = (): 'ios' | 'android' | 'web' => {
  const platform = Platform.OS;

  if (platform === 'ios') return 'ios';
  if (platform === 'android') return 'android';
  return 'web';
};

/**
 * Helper to get current screen name
 * Can be enhanced with navigation context if needed
 *
 * @returns Current screen name or 'Unknown Screen'
 */
export const getCurrentScreenName = (): string => {
  // For now, return undefined - can be enhanced later with navigation state
  // You could use navigation.getCurrentRoute() if you pass navigation context
  return 'Unknown Screen';
};
